import { proxy, ref } from 'valtio';

import type { PlayerStore } from '@cartwhl/3d/player/store/playerStore';

import { noticesStore } from 'stores/global';

import type { ProgressInfo } from './useFfmpeg';

export const motionEditorVideoExportState = proxy({
  recordedVideo: undefined as Blob | undefined,
  isRecording: false,
  videoGenerationProgress: undefined as ProgressInfo | undefined,
});

type RecorderApi = {
  recordVideo: (
    onProgress?: (progress: ProgressInfo) => void,
    signal?: AbortSignal,
  ) => Promise<Blob | undefined>;
};

let registeredRecorder: RecorderApi | null = null;
let recordingAbortController: AbortController | undefined;

export function registerMotionEditorVideoRecorder(recorder: RecorderApi | null) {
  registeredRecorder = recorder;
}

export async function startMotionEditorVideoRecording(
  playerStore: PlayerStore,
): Promise<Blob | undefined> {
  playerStore.pause();
  if (playerStore.state.transport.duration.frames <= 0) {
    noticesStore.error('Add or load motion before exporting video.');
    return undefined;
  }
  if (!registeredRecorder) {
    noticesStore.error('Video recorder is not ready. Try again.');
    return undefined;
  }
  motionEditorVideoExportState.isRecording = true;
  motionEditorVideoExportState.videoGenerationProgress = undefined;

  recordingAbortController = new AbortController();
  const signal = recordingAbortController.signal;

  const progressCallback = (progress: ProgressInfo) => {
    motionEditorVideoExportState.videoGenerationProgress = progress;
  };

  let videoBlob: Blob | undefined;
  try {
    videoBlob = await registeredRecorder.recordVideo(progressCallback, signal);
    if (videoBlob && !signal.aborted) {
      motionEditorVideoExportState.recordedVideo = ref(videoBlob);
    } else if (!signal.aborted) {
      noticesStore.error(
        'Video export did not finish. If this keeps happening, try reloading the page.',
      );
    }
  } catch (error) {
    console.error(error);
    noticesStore.error(
      error instanceof Error
        ? error.message
        : 'Video export failed. Please try again.',
    );
  } finally {
    motionEditorVideoExportState.isRecording = false;
    motionEditorVideoExportState.videoGenerationProgress = undefined;
    recordingAbortController = undefined;
  }

  return videoBlob && !signal.aborted ? videoBlob : undefined;
}

export function resetMotionEditorVideoRecording() {
  recordingAbortController?.abort();
  recordingAbortController = undefined;
  motionEditorVideoExportState.isRecording = false;
  motionEditorVideoExportState.recordedVideo = undefined;
  motionEditorVideoExportState.videoGenerationProgress = undefined;
}
